<template>
  <section class="new-app-packages-table">
    <header class="new-app-packages-table__title">
      {{ $t('APPS.DRAFT.UPLOADED_FILES') }}
    </header>

    <ElTable v-loading="isLoading" :data="packages">
      <ElTableColumn :label="$t('APP.RELEASES.VERSION')" width="200">
        <template slot-scope="{ row: { versionCode, versionName } }">
          {{ versionCode }} ({{ versionName }})
        </template>
      </ElTableColumn>

      <ElTableColumn
        prop="targetApiLevel"
        :label="$t('APP.RELEASES.TARGET_SDK')"
        width="100"
      />

      <ElTableColumn
        prop="apiLevel"
        :label="$t('APP.RELEASES.MIN_SDK')"
        width="100"
      />

      <ElTableColumn
        v-slot="{ row: currentPackage }"
        width="180"
        :label="$t('APP.RELEASES.CPU_ARCHITECTURE')"
      >
        <NewAppPackageArchitecture
          :key="currentPackage.token"
          :package-item="currentPackage"
          :can-edit="!readonly && !isBundleRelease"
        />
      </ElTableColumn>

      <ElTableColumn>
        <div
          slot-scope="{ row: currentPackage }"
          class="flex-container align-right"
        >
          <ElButton
            plain
            size="small"
            @click="openAppPackageDetailsModal(currentPackage)"
          >
            {{ $t('APP.RELEASES.DETAIL_PACKAGE_BTN') }}
          </ElButton>

          <LbDropdown
            v-if="!readonly"
            class="new-app-packages-table__more-button"
            :items="getMoreDropdownItems(currentPackage)"
          >
            <ElButton plain size="small" icon="el-icon-more" />
          </LbDropdown>
        </div>
      </ElTableColumn>
    </ElTable>

    <UsedFeaturesAndLibraries
      v-if="featuresAndLibraries"
      class="new-app-packages-table__alert"
      v-bind="featuresAndLibraries"
    />
  </section>
</template>

<script>
import { DraftApps } from '@/api/api';
import appState from '@/mixins/appState';
import LbDropdown from '@@/common/LbDropdown';
import NewAppPackageArchitecture from './NewAppPackageArchitecture';
import NewAppPackageExpansionsManager from './NewAppPackageExpansionsManager';
import AppPackageDetailsModal from '@@/app/common/AppPackageDetailsModal';
import { mapActions } from 'vuex';
import { UPDATE_DRAFT_APP } from '@/store/types';
import downloadFileWithUrl from '@/utils/downloadFileWithUrl';
import UsedFeaturesAndLibraries from '@/components/app/common/UsedFeaturesAndLibraries';

export default {
  name: 'NewAppPackagesTable',
  components: {
    NewAppPackageArchitecture,
    LbDropdown,
    UsedFeaturesAndLibraries,
  },
  mixins: [appState],
  props: {
    packages: {
      type: Array,
      required: true,
    },
    isBundleRelease: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    featuresAndLibraries: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      isLoading: false,
    };
  },
  methods: {
    ...mapActions({ updateDraftApp: UPDATE_DRAFT_APP }),
    getMoreDropdownItems(packageItem) {
      return [
        !this.isBundleRelease && {
          title: this.$t('APP.RELEASES.REMOVE_PACKAGE'),
          handler: () => this.deletePackage(packageItem),
        },
        {
          title: this.$t('APP.RELEASES.ADD_OBB'),
          handler: () => this.openObbModal(packageItem),
        },
        {
          title: this.$t('APP.RELEASES.DOWNLOAD_PACKAGE'),
          disabled: !packageItem?.isIndependentlyInstallable,
          handler: () => downloadFileWithUrl(packageItem.apkLink),
        },
      ];
    },
    openAppPackageDetailsModal(packageItem) {
      this.$modal.open({
        title: this.$t('APP.RELEASES.DETAIL_MODAL_TITLE'),
        component: AppPackageDetailsModal,
        props: { packageItem },
        width: '25%',
      });
    },
    removeDraftAppPackage(removedPackage) {
      this.isLoading = true;
      const draftAppId = this.$route.params.appId;

      return DraftApps.removePackage({
        id: draftAppId,
        packageId: removedPackage.id,
      })
        .then(({ message, draftApp }) => {
          if (message) this.$message.success(message);
          this.updateDraftApp(draftApp);
        })
        .catch(({ message }) => {
          this.$message.error(message);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    deletePackage(packageItem) {
      return this.$confirm(
        this.$t('APP.RELEASES.REMOVE_PACKAGE_CONFIRMATION'),
        this.$t('WARNING'),
        {
          confirmButtonText: this.$t('DELETE_IT'),
          cancelButtonText: this.$t('CANCEL'),
          cancelButtonClass: 'el-button--text is-plain',
          confirmButtonClass: 'el-button--danger',
          type: 'warning',
        },
      )
        .then(() => {
          this.removeDraftAppPackage(packageItem);
        })
        .catch(() => {});
    },
    openObbModal(packageItem) {
      this.$modal.open({
        title: this.$t('APP.RELEASES.OBB'),
        component: NewAppPackageExpansionsManager,
        props: {
          key: this.$route.params.appId,
          packageId: packageItem.id,
        },
        width: '25%',
      });
    },
  },
};
</script>

<style lang="scss">
.new-app-packages-table {
  position: relative;
  margin-top: 2 * $global-margin;

  &__title {
    font-size: rem-calc(18);
    margin-bottom: $global-margin;
  }

  &__more-button {
    margin-left: calc($global-margin / 2);

    @include rtl() {
      margin-left: 0;
      margin-right: calc($global-margin / 2);
    }
  }

  &__actions {
    display: flex;
    justify-content: flex-end;
  }

  &__alert {
    margin-top: $global-margin * 2;
  }
}
</style>
